import type { LabelHTMLAttributes, ReactNode } from 'react';

import { cnm } from 'utils/classNames';

import LabelText from './LabelText';

interface Props extends LabelHTMLAttributes<HTMLLabelElement> {
	children: ReactNode;
	className?: string;
	disabled?: boolean;
	htmlFor: string;
	invalid?: boolean;
	/**
	 * If the label is currently small and floating above the field border.
	 * Is in addition to cases where it always floats, like on focus.
	 */
	isFloating: boolean;
	isHidden?: boolean;
	required?: boolean;
	valid?: boolean;
}

/**
 * Floating form field label. Is expected to be a sibling after the form
 * field and that field must have the `peer` class name.
 */
export default function FloatingLabel({
	children,
	className,
	disabled = false,
	htmlFor,
	invalid = false,
	isFloating,
	isHidden = false,
	required = false,
	valid = false,
}: Props) {
	return (
		<label
			htmlFor={htmlFor}
			className={
				isHidden
					? 'sr-only top-0 left-0'
					: cnm(
							// Add an additional pixel via margin for exact alignment with the
							// form field (field padding + border).
							'ml-px inline-block px-1',
							'absolute left-3 z-1',
							'max-w-[calc(100%-1.5rem)] overflow-hidden text-ellipsis whitespace-nowrap',
							'rounded-xs',
							'transition-[top,font-size]',
							'pointer-events-none',

							// Color
							'text-subtle',
							!disabled && [invalid && 'text-error', valid && 'text-success'],

							// Size, position and background color
							isFloating ? '-top-2.5 text-sm' : 'top-4 text-md',
							'bg-base',
							disabled && 'bg-disabled',
							isFloating && 'bg-base',
							'peer-focus:-top-2.5 peer-focus:bg-base peer-focus:text-sm',
							'peer-autofill:-top-2.5 peer-autofill:bg-base peer-autofill:text-sm',
							'peer-placeholder-shown:-top-2.5 peer-placeholder-shown:bg-base peer-placeholder-shown:text-sm',

							className,
						)
			}
		>
			<LabelText required={required}>{children}</LabelText>
		</label>
	);
}
FloatingLabel.displayName = 'FloatingLabel';
